import { useState, useEffect, useRef } from 'react';
import UserMixpanel, { Mixpanel } from './MixpanelClass';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { IThisUserState } from '../../store/thisUser/thisUserSlice';

const SESSION_TIMEOUT = 5 * 1000;
interface SessionTrackingProps {
  children: React.ReactNode;
}
const SessionTracking = ({ children }: SessionTrackingProps) => {
  const user: IThisUserState = useSelector(
    (store: RootState) => store?.thisUser
  );
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);
  const sessionIsStarted = useRef<boolean>(false);
  let inactivityTimeout: number | null = null;

  const startSession = () => {
    if (!sessionIsStarted.current && user?.userId) {
      sessionIsStarted.current = true;
      UserMixpanel.getInstance(+user.userId);
      const startTime = new Date();
      setSessionStartTime(startTime);
      Mixpanel.track('session_start', {
        start_time: startTime.toISOString(),
        distinct_id: user.userId,
      });
    }
  };

  const endSession = () => {
    if (sessionIsStarted.current && sessionStartTime) {
      const mx = UserMixpanel.getInstance();
      const endTime = new Date();
      const sessionDuration =
        (endTime.getTime() - sessionStartTime.getTime()) / 1000;
      Mixpanel.track('session_end', {
        task_count: mx.getTotalTaskClickedCount,
        task_groupby_template_count: mx.getTotalTaskGroupbyTemplateClickedCount,
        end_time: endTime.toISOString(),
        session_duration: sessionDuration,
        distinct_id: user.userId,
        roles: mx.getRoles(),
        companyName: mx.getCompanyName(),
      });
      sessionIsStarted.current = false;
    }
  };
  useEffect(() => {
    const startSessionHandler = () => {
      if (inactivityTimeout) {
        clearTimeout(inactivityTimeout);
      }
      startSession();
      inactivityTimeout = window.setTimeout(() => {
        endSession();
      }, SESSION_TIMEOUT);
    };
    const refreshOrClosePageHandler = () => {
      endSession();
      const mixpanelUser = UserMixpanel.getInstance();
      mixpanelUser.sendLogoutTrack();
    };
    if (user.isLoggedIn) {
      startSession();
      inactivityTimeout = window.setTimeout(() => {
        endSession();
      }, SESSION_TIMEOUT);
      window.addEventListener('mousemove', startSessionHandler);
      window.addEventListener('keydown', startSessionHandler);
      window.addEventListener('scroll', startSessionHandler);
      window.addEventListener('focus', startSessionHandler);
      window.addEventListener('beforeunload', refreshOrClosePageHandler);
    }
    if (!user.isLoggedIn && sessionIsStarted.current) {
      endSession();
    }
    return () => {
      window.removeEventListener('mousemove', startSessionHandler);
      window.removeEventListener('keydown', startSessionHandler);
      window.removeEventListener('scroll', startSessionHandler);
      window.removeEventListener('focus', startSessionHandler);
      window.removeEventListener('beforeunload', startSessionHandler);
    };
  }, [user.isLoggedIn]);

  return <>{children}</>;
};

export default SessionTracking;
